import React, { useState } from 'react'
import {
  Box,
  Heading,
  Text,
  Stack,
  Card,
  CardBody,
  CardHeader,
  Button,
  Flex,
  InputGroup,
  Input,
  InputRightElement,
  useToast,
  useColorModeValue,
  Icon,
  Center,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Grid,
} from '@chakra-ui/react'
import { FaCopy, FaEye, FaEyeSlash } from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'

import useAuth from '../hooks/useAuth'
import { runnerService, dataService } from '../services/api'
import RunnerTable from '../components/RunnerTable'
import DataTable from '../components/DataTable'
import UserInfoCard from '../components/UserInformationCard'
import DeployCommandCopy from '../components/DeployCommandCopy'

const ProfilePage: React.FC = () => {
  const { apiKey } = useAuth()
  const toast = useToast()
  const [showApiKey, setShowApiKey] = useState(false)

  const cardBg = useColorModeValue('white', 'gray.700')

  const { data, isLoading, error } = useQuery({
    queryKey: ['runners'],
    queryFn: async () => {
      const response = await runnerService.getRunnerList()
      return response.data || []
    },
  })

  const {
    data: records,
    isLoading: isRecordsLoading,
    error: recordsError,
  } = useQuery({
    queryKey: ['dataRecords'],
    queryFn: async () => {
      const response = await dataService.getDataRecords()
      return response.data || []
    },
  })

  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey)
      toast({
        title: 'API Key Copied',
        description: 'The API key has been copied to your clipboard.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const toggleShowApiKey = () => {
    setShowApiKey(!showApiKey)
  }

  return (
    <Box p={4}>
      <Heading size="lg" mb={6}>
        Profile
      </Heading>

      <Stack spacing={6}>
        <Grid templateColumns={{ base: '1fr', md: '60% 40%' }} gap={4}>
          {/* User Information Card */}
          <UserInfoCard />
          {/* API Key */}
          <Card bg={cardBg} boxShadow="md">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md">API Key</Heading>
              </Flex>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <Text color="gray.500" fontSize="sm">
                  You can use this API key for authenticating API requests.
                </Text>

                <InputGroup>
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey || ''}
                    readOnly
                    pr="4.5rem"
                    fontFamily="monospace"
                  />
                  <InputRightElement width="4.5rem">
                    <Button h="1.75rem" size="sm" onClick={toggleShowApiKey}>
                      <Icon as={showApiKey ? FaEyeSlash : FaEye} />
                    </Button>
                  </InputRightElement>
                </InputGroup>

                <Button
                  leftIcon={<Icon as={FaCopy} />}
                  onClick={handleCopyApiKey}
                  colorScheme="blue"
                  width="full"
                >
                  Copy API Key
                </Button>
              </Stack>
            </CardBody>
          </Card>

          {/* Deploy Runner Command */}
          <Card bg={cardBg} boxShadow="md">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md">Deploy Runner Command</Heading>
              </Flex>
            </CardHeader>
            <CardBody>
              <Stack spacing={4}>
                <Text color="gray.500" fontSize="sm">
                  Use this command to deploy a runner with your API key.
                </Text>
                <DeployCommandCopy apiKey={apiKey || ''} />
              </Stack>
            </CardBody>
          </Card>
        </Grid>
        {/* Upcoming Features */}
        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Runner Table</Heading>
          </CardHeader>
          <CardBody overflow={"auto"}>
            {isLoading && !data ? (
              <Center h="60vh">
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : (
              <RunnerTable
                // @ts-expect-error assignable
                data={data}
                isLoading={isLoading}
                error={error}
              />
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader>
            <Heading size="md">Records Table</Heading>
          </CardHeader>
          <CardBody>
            <Tabs variant="enclosed" colorScheme="blue">
              <TabList>
                <Tab>
                  Data Records
                  {/* @ts-ignore */}
                  {records?.data_records?.length > 0 && (
                    <Badge ml={2} colorScheme="blue" borderRadius="full">
                      {/* @ts-ignore */}
                      {records.data_records.length}
                    </Badge>
                  )}
                </Tab>
                <Tab>
                  Alpha Records
                  {/* @ts-ignore */}
                  {records?.alpha_records?.length > 0 && (
                    <Badge ml={2} colorScheme="purple" borderRadius="full">
                      {/* @ts-ignore */}
                      {records.alpha_records.length}
                    </Badge>
                  )}
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel p={0} pt={4}>
                  <DataTable
                    data={records?.data_records || []}
                    isLoading={isRecordsLoading}
                    error={recordsError}
                    recordType="Data"
                  />
                </TabPanel>
                <TabPanel p={0} pt={4}>
                  <DataTable
                    data={records?.alpha_records || []}
                    isLoading={isRecordsLoading}
                    error={recordsError}
                    recordType="Alpha"
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </Stack>
    </Box>
  )
}

export default ProfilePage
